import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import LoadingSpinner from "@/features/Common/components/LoadingSpinner";
import MaxWidthContainer from "@/features/Common/components/MaxWidthContainer";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";
import { trackPageViewV2 } from "@/services/analytics-adapter";
import theme, { shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FindAFacilityResults from "./components/results";
import { PathologyFacility } from "@/services/core-api-adapter";

interface FindAFacilityProps {
  onBackButton: () => void;
}

interface SelectedProvinceProps {
  selectedProvince: string;
}

const mockFacilityData = [
  {
    facilityName: "Ampath Midstream Lab",
    facilityAddress: "Midstream Estate, Centurion, 1692",
    facilityContactNumber: "012 661 0364",
    facilityLatitude: -25.885,
    facilityLongitude: 28.206,
    province: "Gauteng",
    town: "Centurion",
    openingTimes: [
      {
        day: "Monday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Tuesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Wednesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Thursday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Friday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Saturday",
        hours: "07:00 - 12:00",
      },
      {
        day: "Sunday",
        hours: "Closed",
      },
      {
        day: "Public holidays",
        hours: "Closed",
      },
    ],
  },
  {
    facilityName: "Lancet Laboratories",
    facilityAddress: "123 Main Street, Johannesburg, 2000",
    facilityContactNumber: "011 123 4567",
    facilityLatitude: -26.2041,
    facilityLongitude: 28.0473,
    province: "Gauteng",
    town: "Johannesburg",
    openingTimes: [
      {
        day: "Monday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Tuesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Wednesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Thursday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Friday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Saturday",
        hours: "08:00 - 12:00",
      },
      {
        day: "Sunday",
        hours: "Closed",
      },
      {
        day: "Public holidays",
        hours: "Closed",
      },
    ],
  },
  {
    facilityName: "PathCare",
    facilityAddress: "456 High Street, Cape Town, 8000",
    facilityContactNumber: "021 456 7890",
    facilityLatitude: -33.9249,
    facilityLongitude: 18.4241,
    province: "Western Cape",
    town: "Cape Town",
    openingTimes: [
      {
        day: "Monday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Tuesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Wednesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Thursday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Friday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Saturday",
        hours: "08:00 - 12:00",
      },
      {
        day: "Sunday",
        hours: "Closed",
      },
      {
        day: "Public holidays",
        hours: "Closed",
      },
    ],
  },
  {
    facilityName: "NHLS",
    facilityAddress: "789 Park Avenue, Durban, 4001",
    facilityContactNumber: "031 789 0123",
    facilityLatitude: -29.8587,
    facilityLongitude: 31.0218,
    province: "KwaZulu-Natal",
    town: "Durban",
    openingTimes: [
      {
        day: "Monday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Tuesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Wednesday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Thursday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Friday",
        hours: "07:00 - 17:00",
      },
      {
        day: "Saturday",
        hours: "08:00 - 12:00",
      },
      {
        day: "Sunday",
        hours: "Closed",
      },
      {
        day: "Public holidays",
        hours: "Closed",
      },
    ],
  },
];

function SelectedProvince({ selectedProvince }: SelectedProvinceProps) {
  const { t } = useTranslation();

  return (
    <Stack direction="row">
      <WatermarkIcon
        extra={{
          alignSelf: "center",
        }}
        height={35}
        width={35}
        iconSize="medium"
        color={theme.palette.accent2.light}
        foreground={theme.palette.primary[700]}
        Icon={"MapMarkerIcon"}
      />
      <Stack ml={2} spacing={1}>
        <Typography
          variant="body2"
          fontWeight={600}
          fontSize={convertPxToRem(10)}
          color={theme.palette.neutral[700]}
        >
          {t("FindADoctor.selectedProvinceLabel")}
        </Typography>
        <Typography fontSize={convertPxToRem(16)} variant="h2">
          {selectedProvince}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function FindAFacility({ onBackButton }: FindAFacilityProps) {
  const { t } = useTranslation();
  const [selectedProvinceValue, setSelectedProvinceValue] = useState("");
  const [shouldShowResults, setShouldShowResults] = useState(false);
  const [shouldShowProvinces, setShouldShowProvinces] = useState(true);
  const [searchResults, setSearchResults] = useState({});
  const [provincesList, setProvincesList] = useState<string[]>([]);

  const isFacilityListLoading = false;
  const facilityListFetchError = false;
  const facilityList = mockFacilityData;

  useEffect(() => {
    trackPageViewV2({
      pageName: "Pathology:Find a facility",
      pageSubSection1: "Pathology",
      pageSubSection2: "Pathology:Find a facility",
      pageCategory: "Pathology",
    });
  }, []);

  function sortResultsByProvince(
    results: PathologyFacility[],
    selectedProvince: string
  ): PathologyFacility[] {
    return results.filter(
      (resultItem: PathologyFacility) =>
        resultItem.province === selectedProvince
    );
  }

  function getProvinceNames(results: any): string[] {
    const provinceNames: string[] = [];

    results?.forEach((resultItem: PathologyFacility) => {
      if (!provinceNames.includes(resultItem.province)) {
        provinceNames.push(resultItem.province);
      }
    });

    return provinceNames;
  }

  const handleProvinceSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedProvinceValue((event.target as HTMLInputElement).value);
  };

  function onHandleCancelButtonClick() {
    if (selectedProvinceValue && !shouldShowResults) {
      setSelectedProvinceValue("");
    }

    if (selectedProvinceValue && Object.keys(searchResults).length) {
      return setShouldShowResults(false);
    }
  }

  function onContinueButtonClick() {
    if (selectedProvinceValue) {
      setShouldShowResults(true);
      setShouldShowProvinces(false);

      const sortedResults = sortResultsByProvince(
        facilityList,
        selectedProvinceValue
      );
      setSearchResults(sortedResults);
    }
  }

  function onBackButtonClick() {
    if (shouldShowResults) {
      setShouldShowResults(false);
      setShouldShowProvinces(true);
      setSelectedProvinceValue("");
      return;
    }
    onBackButton();
  }

  useMemo(() => {
    if (facilityList && facilityList.length > 0) {
      const provinces = getProvinceNames(facilityList);
      setProvincesList(provinces);
      setShouldShowProvinces(true);
    }
  }, [facilityList]);

  if (!isFacilityListLoading && facilityListFetchError) {
    return (
      <Typography textAlign="center">
        {t("common.somethingWentWrong")}
      </Typography>
    );
  }

  return (
    <>
      <Stack
        color="neutral.dark"
        pb={12}
        sx={{
          height: "100%",
        }}
      >
        <Box p={2}>
          <Stack>
            <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
          </Stack>

          <Stack>
            <IconLoader
              icon="ChatWithMapAndUnuIcon"
              color="primary"
              sx={{
                fontSize: convertPxToRem(150),
                alignSelf: "center",
              }}
            />

            <Stack textAlign="center" spacing={2} py={4}>
              <Typography variant="h1" color="inherit">
                {t(`FindAFacility.title`)}
              </Typography>

              <Typography variant="h3" color="inherit" px={2}>
                {shouldShowResults
                  ? t("FindAFacility.selectSuburbLabel")
                  : t(`FindAFacility.subtitle`)}
              </Typography>

              <Divider sx={{ pt: 2 }} />
            </Stack>
          </Stack>
        </Box>

        {isFacilityListLoading && (
          <Stack alignItems="center">
            <LoadingSpinner />
          </Stack>
        )}
        {!isFacilityListLoading && (
          <Stack spacing={2}>
            <Stack px={2} spacing={4}>
              {shouldShowResults ? (
                <SelectedProvince selectedProvince={selectedProvinceValue} />
              ) : (
                <Typography
                  fontWeight="600"
                  color="neutral.700"
                  variant="body2"
                >
                  {t(`FindAFacility.provincesLabel`)}
                </Typography>
              )}

              {shouldShowResults && (
                <Stack pb={{ xs: convertPxToRem(50), md: 0 }}>
                  <FindAFacilityResults
                    facilitySearchResults={searchResults as any[]}
                  />
                </Stack>
              )}
            </Stack>

            {shouldShowProvinces && (
              <Stack spacing={2} px={4} pb={{ xs: convertPxToRem(100), md: 0 }}>
                {provincesList.map((provinceText, index) => (
                  <FormControl key={String(provinceText) + String(index)}>
                    <RadioGroup
                      aria-labelledby={t(`FindAFacility.provincesLabel`)}
                      value={selectedProvinceValue}
                      onChange={handleProvinceSelectionChange}
                    >
                      <FormControlLabel
                        value={provinceText}
                        control={<Radio />}
                        label={
                          <Typography fontWeight="500" color="neutral.700">
                            {provinceText}
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      {!shouldShowResults && (
        <Stack
          direction={"row"}
          spacing={1}
          p={2}
          bgcolor="background.paper"
          width="100%"
          justifyContent="center"
          textAlign="center"
          alignItems="center"
          left="0"
          bottom="0"
          position="fixed"
          sx={{
            boxShadow: shadows.heavy,
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
          }}
        >
          <MaxWidthContainer>
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Button
                onClick={onHandleCancelButtonClick}
                color="primary"
                variant="outlined"
                fullWidth
              >
                {t("common.cancelButton")}
              </Button>
              <Button
                disabled={!selectedProvinceValue}
                onClick={onContinueButtonClick}
                fullWidth
              >
                {t("common.continueButton")}
              </Button>
            </Stack>
          </MaxWidthContainer>
        </Stack>
      )}
    </>
  );
}
